import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { listOrganizations, listRestRules, listUsers as listAppUsers } from "../graphql/queries";
import { createOrganization, deleteOrganization, updateOrganization } from "../graphql/mutations";
import { formatApiError } from "../lib/formatApiError";
import AppDialog from "../components/AppDialog";
import { buildAdminScope } from "../lib/adminScope";

interface Organization {
  id: string;
  name: string;
  restRuleId?: string | null;
  admins?: string[] | null;
}

interface RestRule {
  id: string;
  name: string;
  global?: boolean | null;
  organizationId?: string | null;
}

interface AppUser {
  id: string;
  email?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  roles?: string[] | null;
}

interface OrganizationsPageProps {
  userRoles: string[];
}

const OrganizationsPage: React.FC<OrganizationsPageProps> = ({ userRoles }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [rules, setRules] = useState<RestRule[]>([]);
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);
  const [username, setUsername] = useState("");
  const [newName, setNewName] = useState("");
  const [newRestRuleId, setNewRestRuleId] = useState("");
  const [newAdmins, setNewAdmins] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingRestRuleId, setEditingRestRuleId] = useState("");
  const [editingAdmins, setEditingAdmins] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterText, setFilterText] = useState("");
  const [filterHasRule, setFilterHasRule] = useState<"all" | "yes" | "no">("all");
  const [sortKey, setSortKey] = useState<"name" | "rule">("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const adminScope = useMemo(
    () => buildAdminScope({ userRoles, username, organizations }),
    [organizations, userRoles, username]
  );
  const canManage = adminScope.isGlobalAdmin || adminScope.isScopedCommish;

  const loadOrganizations = async (silent = false) => {
    try {
      if (!silent) {
        setErrorMessage("");
      }
      const authUser = await Auth.currentAuthenticatedUser();
      setUsername(authUser?.username ?? "");
      const [orgResult, rulesResult, usersResult] = await Promise.all([
        API.graphql(graphqlOperation(listOrganizations, { limit: 200 })),
        API.graphql(graphqlOperation(listRestRules, { limit: 200 })),
        API.graphql(graphqlOperation(listAppUsers, { limit: 500 }))
      ]);
      const orgItems = ((orgResult as any)?.data?.listOrganizations?.items ?? []).filter(Boolean);
      const ruleItems = ((rulesResult as any)?.data?.listRestRules?.items ?? []).filter(Boolean);
      const appUserItems = ((usersResult as any)?.data?.listUsers?.items ?? []).filter(Boolean);
      setOrganizations(orgItems);
      setRules(ruleItems);
      setAppUsers(appUserItems);
    } catch (error) {
      if (!silent) {
        setErrorMessage(`Failed to load organizations: ${formatApiError(error)}`);
      }
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      return;
    }
    if (!adminScope.canCreateOrganizations) {
      setErrorMessage("Only Commish and SuperAdmin can create organizations.");
      return;
    }

    try {
      setErrorMessage("");
      const result: any = await API.graphql(
        graphqlOperation(createOrganization, {
          input: {
            name: newName.trim(),
            restRuleId: newRestRuleId || null,
            admins: newAdmins
          }
        })
      );
      if (!result?.data?.createOrganization) {
        throw new Error(formatApiError(result));
      }
      setNewName("");
      setNewRestRuleId("");
      setNewAdmins([]);
      setCreateOpen(false);
      await loadOrganizations(true);
    } catch (error) {
      setErrorMessage(`Failed to create organization: ${formatApiError(error)}`);
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !editingName.trim()) {
      return;
    }
    if (!adminScope.canManageOrganization(editingId)) {
      setErrorMessage("Commish can only edit assigned organizations.");
      return;
    }

    try {
      setErrorMessage("");
      const result: any = await API.graphql(
        graphqlOperation(updateOrganization, {
          input: {
            id: editingId,
            name: editingName.trim(),
            restRuleId: editingRestRuleId || null,
            admins: editingAdmins
          }
        })
      );
      if (!result?.data?.updateOrganization) {
        throw new Error(formatApiError(result));
      }
      setEditingId(null);
      setEditingName("");
      setEditingRestRuleId("");
      setEditingAdmins([]);
      setEditOpen(false);
      await loadOrganizations(true);
    } catch (error) {
      setErrorMessage(`Failed to update organization: ${formatApiError(error)}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this organization?")) {
      return;
    }
    if (!adminScope.canManageOrganization(id)) {
      setErrorMessage("Commish can only delete assigned organizations.");
      return;
    }

    try {
      setErrorMessage("");
      const result: any = await API.graphql(graphqlOperation(deleteOrganization, { input: { id } }));
      if (!result?.data?.deleteOrganization) {
        throw new Error(formatApiError(result));
      }
      await loadOrganizations(true);
    } catch (error) {
      setErrorMessage(`Failed to delete organization: ${formatApiError(error)}`);
    }
  };

  const availableRulesByOrg = useMemo(() => {
    const out: Record<string, RestRule[]> = {};
    for (const org of organizations) {
      out[org.id] = rules.filter((rule) => rule.global || rule.organizationId === org.id);
    }
    return out;
  }, [organizations, rules]);
  const ruleNameById = useMemo(
    () => rules.reduce<Record<string, string>>((acc, rule) => ({ ...acc, [rule.id]: rule.name }), {}),
    [rules]
  );
  const globalRules = useMemo(() => rules.filter((rule) => rule.global), [rules]);
  const availableCommishes = useMemo(
    () =>
      appUsers
        .filter((user) => (user.roles ?? []).some((role) => ["SuperAdmin", "Commish"].includes(role)))
        .sort((a, b) => {
          const aLabel = `${a.givenName ?? ""} ${a.familyName ?? ""}`.trim() || a.email || a.id;
          const bLabel = `${b.givenName ?? ""} ${b.familyName ?? ""}`.trim() || b.email || b.id;
          return aLabel.localeCompare(bLabel);
        }),
    [appUsers]
  );
  const userLabelById = useMemo(
    () =>
      appUsers.reduce<Record<string, string>>((acc, user) => {
        acc[user.id] = `${user.givenName ?? ""} ${user.familyName ?? ""}`.trim() || user.email || user.id;
        return acc;
      }, {}),
    [appUsers]
  );
  const summarizeAdmins = (admins: string[] | null | undefined) => {
    const resolved = (admins ?? []).map((id) => userLabelById[id] ?? id).filter(Boolean);
    if (resolved.length === 0) {
      return "None";
    }
    if (resolved.length <= 2) {
      return resolved.join(", ");
    }
    return `${resolved.slice(0, 2).join(", ")} +${resolved.length - 2}`;
  };

  const visibleOrganizations = useMemo(
    () => organizations.filter((organization) => adminScope.isGlobalAdmin || adminScope.canManageOrganization(organization.id)),
    [adminScope, organizations]
  );

  const sortedOrganizations = useMemo(() => {
    const normalizedText = filterText.trim().toLowerCase();
    const filtered = visibleOrganizations.filter((organization) => {
      const hasRule = Boolean(organization.restRuleId);
      if (normalizedText && !organization.name.toLowerCase().includes(normalizedText)) {
        return false;
      }
      if (filterHasRule === "yes" && !hasRule) {
        return false;
      }
      if (filterHasRule === "no" && hasRule) {
        return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      const compare = sortKey === "rule" ? (a.restRuleId ? 1 : 0) - (b.restRuleId ? 1 : 0) : a.name.localeCompare(b.name);
      return sortDirection === "asc" ? compare : -compare;
    });
  }, [filterHasRule, filterText, sortDirection, sortKey, visibleOrganizations]);

  const toggleSort = (key: "name" | "rule") => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  useEffect(() => {
    loadOrganizations();
  }, []);

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Organizations</h1>
      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}

      {adminScope.canCreateOrganizations && (
        <div className="bubble-card p-4 mb-4 flex gap-2">
          <button onClick={() => setCreateOpen(true)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            Add Organization
          </button>
        </div>
      )}

      <div className="bubble-card p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-2">
        <input
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          placeholder="Filter by organization name"
          className="border px-3 py-2 rounded"
        />
        <select value={filterHasRule} onChange={(event) => setFilterHasRule(event.target.value as "all" | "yes" | "no")} className="border px-3 py-2 rounded">
          <option value="all">All rule states</option>
          <option value="yes">With assigned rule</option>
          <option value="no">Without assigned rule</option>
        </select>
      </div>

      <div className="bubble-table-wrapper">
        <table className="bubble-table">
          <thead>
            <tr>
              <th><button className="table-sort" onClick={() => toggleSort("name")}>Organization</button></th>
              <th>Commishes</th>
              <th><button className="table-sort" onClick={() => toggleSort("rule")}>Rule</button></th>
              {canManage && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedOrganizations.length === 0 && (
              <tr>
                <td colSpan={canManage ? 4 : 3} className="text-gray-600">No organizations found.</td>
              </tr>
            )}
            {sortedOrganizations.map((organization) => (
              <tr key={organization.id}>
                <td>{organization.name}</td>
                <td>{summarizeAdmins(organization.admins)}</td>
                <td>{organization.restRuleId ? (ruleNameById[organization.restRuleId] ?? "Unknown rule") : "None"}</td>
                {canManage && adminScope.canManageOrganization(organization.id) && (
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingId(organization.id);
                          setEditingName(organization.name);
                          setEditingRestRuleId(organization.restRuleId ?? "");
                          setEditingAdmins(organization.admins?.filter(Boolean) ?? []);
                          setEditOpen(true);
                        }}
                        className="bg-yellow-500 text-white px-3 py-1 rounded"
                      >
                        Edit
                      </button>
                      <button onClick={() => handleDelete(organization.id)} className="bg-red-600 text-white px-3 py-1 rounded">
                        Delete
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add Organization"
        footer={
          <>
            <button onClick={() => setCreateOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={handleCreate} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            type="text"
            value={newName}
            placeholder="Organization name"
            onChange={(event) => setNewName(event.target.value)}
            className="border px-3 py-2 w-full rounded"
          />
          <select value={newRestRuleId} onChange={(event) => setNewRestRuleId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No assigned rule</option>
            {globalRules.map((rule) => (
              <option key={rule.id} value={rule.id}>{rule.name}</option>
            ))}
          </select>
        </div>
        <div className="user-role-picker">
          <div className="user-role-picker__label">Commishes</div>
          <div className="user-role-picker__grid">
            {availableCommishes.map((user) => (
              <label key={user.id} className="user-role-picker__item">
                <input
                  type="checkbox"
                  checked={newAdmins.includes(user.id)}
                  onChange={(event) =>
                    setNewAdmins((current) =>
                      event.target.checked ? [...current, user.id] : current.filter((candidate) => candidate !== user.id)
                    )
                  }
                />
                <span>{userLabelById[user.id] ?? user.id}</span>
              </label>
            ))}
          </div>
        </div>
      </AppDialog>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Organization"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={handleUpdate} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            value={editingName}
            onChange={(event) => setEditingName(event.target.value)}
            className="border px-3 py-2 rounded w-full"
            placeholder="Organization name"
          />
          <select value={editingRestRuleId} onChange={(event) => setEditingRestRuleId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No assigned rule</option>
            {(editingId ? availableRulesByOrg[editingId] ?? [] : []).map((rule) => (
              <option key={rule.id} value={rule.id}>{rule.name}</option>
            ))}
          </select>
        </div>
        <div className="user-role-picker">
          <div className="user-role-picker__label">Commishes</div>
          <div className="user-role-picker__grid">
            {availableCommishes.map((user) => (
              <label key={user.id} className="user-role-picker__item">
                <input
                  type="checkbox"
                  checked={editingAdmins.includes(user.id)}
                  onChange={(event) =>
                    setEditingAdmins((current) =>
                      event.target.checked ? [...current, user.id] : current.filter((candidate) => candidate !== user.id)
                    )
                  }
                />
                <span>{userLabelById[user.id] ?? user.id}</span>
              </label>
            ))}
          </div>
        </div>
      </AppDialog>
    </div>
  );
};

export default OrganizationsPage;
